<template>
  <themed-modal
    name="create-subsidiary"
    :title="subsidiaryId ? 'Edit Subsidiary' : 'Create Subsidiary'"
    size="md"
    @closed="handleClosed"
  >
    <themed-form
      :fields="subsidiaryFields"
      :model-value="subsidiary"
      :errors="errors"
      layout="grid"
      :columns="12"
      @input="subsidiary = $event"
    />

    <template #footer>
      <div class="modal-actions">
        <themed-button
          variant="outline"
          @click="hideModal"
          :disabled="loading"
        >
          Close
        </themed-button>
        <themed-button
          @click="save"
          :disabled="loading"
        >
          {{ loading ? 'Saving...' : (subsidiaryId ? 'Update' : 'Save') }}
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'CreateSubsidiary',
  
  components: {
    ThemedModal,
    ThemedForm,
    ThemedButton
  },
  
  props: {
    clientId: {
      type: Number,
      required: false,
      default: null
    },

    subsidiaryId: {
      type: Number,
      required: false,
      default: null
    }
  },
  
  data() {
    return {
      subsidiary: {
        client_id: this.clientId || window.user.client.id,
        country_id: 16,
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: ''
      },
      countries: [],
      states: [],
      errors: {},
      loading: false
    };
  },

  computed: {
    ...mapState(['subsidiaries']),
    
    subsidiaryFields() {
      const fields = [
        // Name - full width (12 columns)
        {
          name: 'name',
          type: 'input',
          label: 'Name',
          required: true,
          col: 12
        },
        // Address - full width (12 columns)
        {
          name: 'address',
          type: 'input',
          label: 'Address',
          required: true,
          col: 12
        },
        // City - 3 columns
        {
          name: 'city',
          type: 'input',
          label: 'City',
          required: true,
          col: 3
        }
      ];
      
      // State - 2 columns (only for USA)
      if (this.subsidiary.country_id == 16) {
        fields.push({
          name: 'state',
          type: 'select',
          label: 'ST',
          options: this.stateOptions,
          required: true,
          col: 2
        });
      }
      
      // Zip - 3 or 4 columns (depending on whether state is shown)
      fields.push({
        name: 'postal_code',
        type: 'input',
        label: 'Zip',
        required: true,
        col: this.subsidiary.country_id == 16 ? 3 : 4
      });
      
      // Country - 4 or 5 columns (depending on whether state is shown)
      fields.push({
        name: 'country_id',
        type: 'select',
        label: 'Country',
        options: this.countryOptions,
        required: true,
        col: this.subsidiary.country_id == 16 ? 4 : 5
      });
      
      return fields;
    },
    
    countryOptions() {
      return this.countries.map(country => ({
        value: country.id,
        label: country.name
      }));
    },
    
    stateOptions() {
      return this.states.map(state => ({
        value: state.state,
        label: state.state
      }));
    }
  },
  
  mounted() {
    this.loadAddressInfo();
    if (this.subsidiaryId) {
      this.loadSubsidiary();
    }
  },
  
  watch: {
    subsidiaryId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadSubsidiary();
        } else {
          // Reset for create mode
          this.subsidiary = {
            client_id: this.clientId || window.user.client.id,
            country_id: 16,
            name: '',
            address: '',
            city: '',
            state: '',
            postal_code: ''
          };
        }
      }
    }
  },
  
  methods: {
    loadSubsidiary() {
      const subsidiaryData = _.find(this.subsidiaries.all, (sub) => sub.id == this.subsidiaryId);
      if (subsidiaryData) {
        this.subsidiary = _.clone(subsidiaryData);
      }
    },

    async loadAddressInfo() {
      try {
        const response = await axios.get('/api/address-info');
        this.countries = response.data.countries;
        this.states = response.data.states;
      } catch (error) {
        console.error('Error loading address info:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load address information'
        });
      }
    },
    
    hideModal() {
      this.$modal.hide('create-subsidiary');
    },
    
    handleClosed() {
      this.subsidiary = {
        client_id: this.clientId || window.user.client.id,
        country_id: 16,
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: ''
      };
      this.errors = {};
      this.loading = false;
    },
    
    async save() {
      this.loading = true;
      this.errors = {};
      
      try {
        let response;
        if (this.subsidiaryId) {
          response = await axios.patch('/subsidiaries/' + this.subsidiaryId, this.subsidiary);
          this.$emit('subsidiaryUpdated', response.data.subsidiary);
        } else {
          response = await axios.post('/subsidiaries', this.subsidiary);
          this.$emit('subsidiaryCreated', response.data.subsidiary);
        }
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || (this.subsidiaryId ? 'Subsidiary updated successfully' : 'Subsidiary created successfully')
        });
        
        this.hideModal();
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
        
        const message = error.response?.data?.message || (this.subsidiaryId ? 'Failed to update subsidiary' : 'Failed to create subsidiary');
        this.$notify({
          type: 'error',
          title: 'Error',
          text: message
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
